import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { mapProductsToItemData } from '@/shared/api/mappers/product';
import type { ProductSort, ProductStatus } from '@/shared/api/types/generated';
import { useGetProductFeedQuery } from '@/shared/hooks/queries/generated';
import i18n from '@/shared/i18n';
import { type AnalysisSegment, toSegmentInput } from '@/shared/utils/analysis/segment';

interface UseProductFeedInfiniteParams {
  analysisId: string;
  status: ProductStatus;
  sort?: ProductSort;
  /** Filtre marque serveur (une seule — le multi se fait client sur les pages chargées). */
  brand?: string;
  /** Recherche titre/marque serveur — appliquée avant la pagination, quel que soit le tri. */
  searchText?: string;
  segment?: AnalysisSegment;
  enabled?: boolean;
}

const PAGE_SIZE = 50;

/**
 * Feed paginé serveur (scroll infini) — snapshot figé, filtres de segment
 * appliqués côté serveur (args plats de getProductFeed).
 */
export const useProductFeedInfinite = ({
  analysisId,
  status,
  sort,
  brand,
  searchText,
  segment,
  enabled = true,
}: UseProductFeedInfiniteParams) => {
  const segmentInput = segment ? toSegmentInput(segment) : null;

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['GetProductFeed', analysisId, status, sort ?? 'newest', brand ?? null, searchText ?? null, segmentInput],
    queryFn: ({ pageParam }) =>
      useGetProductFeedQuery.fetcher({
        analysisId,
        status,
        limit: PAGE_SIZE,
        cursor: pageParam,
        sort: sort ?? null,
        brand: brand ?? null,
        searchText: searchText ?? null,
        conditionIds: segmentInput?.conditionIds ?? null,
        sizeIds: segmentInput?.sizeIds ?? null,
        colorIds: segmentInput?.colorIds ?? null,
        countryIds: segmentInput?.countryIds ?? null,
        priceMin: segmentInput?.priceMin ?? null,
        priceMax: segmentInput?.priceMax ?? null,
      })(),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.getProductFeed.hasNextPage ? (lastPage.getProductFeed.nextCursor ?? undefined) : undefined,
    enabled: enabled && !!analysisId,
    // Snapshot figé à la création : jamais périmé
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });

  const products = useMemo(
    () => mapProductsToItemData(data?.pages.flatMap((page) => page.getProductFeed.products) ?? []),
    [data],
  );

  return {
    products,
    isLoading,
    error: error ? i18n.t('salesFeed.loadError') : null,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
};
